// Top navigation bar — solid Salesforce-blue (#032d60), pixel-true to
// the prototype shell.jsx Nav. Now wired to real org auth: shows the
// connected org's host with a green pulse when a session exists, or a
// "Connect org" CTA when not. Curator + Admin links appear conditionally.

import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { useAuth } from "@/hooks/useAuth";
import { notificationsApi } from "@/lib/api/notifications";
import { useUiStore } from "@/lib/store/uiStore";

interface NavItem {
  to: string;
  label: string;
  // When set, only show if the predicate returns true.
  when?: (auth: ReturnType<typeof useAuth>) => boolean;
}

// Items in the second array (org-gated) only show once a Salesforce org
// session is active. The "Build" tab is always available because it IS
// the connect gate when unauthenticated.
const ITEMS: NavItem[] = [
  { to: "/", label: "Build" },
  { to: "/runs", label: "Runs", when: (a) => a.orgConnected },
  { to: "/profiles", label: "Profiles", when: (a) => a.orgConnected },
  { to: "/templates", label: "Quick Builds", when: (a) => a.orgConnected },
  { to: "/explore", label: "Explore", when: (a) => a.orgConnected },
  { to: "/playbooks", label: "Playbooks", when: (a) => a.orgConnected },
  { to: "/coach", label: "AI Coach", when: (a) => a.orgConnected },
  { to: "/curator", label: "Curator", when: (a) => a.isCurator },
  { to: "/admin", label: "Admin", when: (a) => a.isAdmin },
  // Docs lives at the end and is always visible — a brand-new SE who
  // hasn't connected yet still needs the Quick Start + connect-flow
  // help text.
  { to: "/docs", label: "Docs" },
];

function trimHost(url?: string): string {
  if (!url) return "";
  try {
    return new URL(url).host;
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

export function Nav(): JSX.Element {
  const setAiGenOpen = useUiStore((s) => s.setAiGenOpen);
  const auth = useAuth();
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const visibleItems = ITEMS.filter((item) => !item.when || item.when(auth));

  return (
    <nav
      className="sf-nav"
      style={{
        height: 52,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        position: "sticky",
        top: 0,
        zIndex: 60,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        <NavLink
          to="/"
          style={{
            background: "transparent",
            border: 0,
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: 0,
            textDecoration: "none",
          }}
        >
          <img
            src="/assets/tableau-logo.png"
            width={22}
            height={22}
            style={{ borderRadius: 4 }}
            alt=""
          />
          <span style={{ fontSize: 14, fontWeight: 600 }}>Generator</span>
          <span style={{ fontSize: 10, color: "#8BA4BE", marginLeft: 4 }}>
            by Tableau Q Branch
          </span>
        </NavLink>

        <div style={{ display: "flex", gap: 18 }}>
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "transition-colors",
                  isActive ? "font-semibold" : "font-normal",
                )
              }
              style={({ isActive }) => ({
                background: "transparent",
                border: 0,
                color: isActive ? "white" : "#AACBE2",
                fontSize: 13,
                padding: 0,
                textDecoration: "none",
              })}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          color: "white",
          fontSize: 12,
        }}
      >
        {auth.orgConnected && (
          <button
            onClick={() => setAiGenOpen(true)}
            style={{
              background: "linear-gradient(135deg, #fff, #e6f0ff)",
              color: "var(--eb-15)",
              border: 0,
              padding: "6px 12px",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>✦</span>
            Generate
          </button>
        )}

        {auth.orgConnected && (
          <NotifBell open={notifOpen} setOpen={setNotifOpen} />
        )}

        {auth.loading ? (
          <span style={{ color: "#8BA4BE" }}>…</span>
        ) : auth.orgConnected ? (
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setOrgMenuOpen((v) => !v)}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "white",
                padding: "5px 10px",
                borderRadius: 999,
                fontSize: 12,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ color: "#4BC076" }}>●</span>
              <span style={{ color: "#dbeaf7" }}>
                {trimHost(auth.org?.instance_url) || auth.org?.org_name || "Connected"}
              </span>
              <span style={{ opacity: 0.6, fontSize: 10 }}>▾</span>
            </button>
            {orgMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 6px)",
                  background: "white",
                  color: "var(--ink)",
                  borderRadius: 8,
                  boxShadow: "0 12px 32px -10px rgba(3,45,96,.35)",
                  border: "1px solid var(--bd)",
                  minWidth: 240,
                  zIndex: 70,
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--bd)" }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{auth.org?.org_name}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                    {trimHost(auth.org?.instance_url)}
                  </div>
                  {(auth.isAdmin || auth.isCurator) && (
                    <div style={{ marginTop: 6, display: "flex", gap: 4 }}>
                      {auth.isAdmin && <span className="chip soft-violet" style={{ fontSize: 10 }}>Admin</span>}
                      {auth.isCurator && !auth.isAdmin && (
                        <span className="chip soft-violet" style={{ fontSize: 10 }}>Curator</span>
                      )}
                    </div>
                  )}
                </div>
                <a
                  href="/api/org/launch?path=/lightning/page/home"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOrgMenuOpen(false)}
                  style={{ ...menuBtn, textDecoration: "none", display: "block" }}
                >
                  Open org ↗
                </a>
                <button
                  onClick={() => {
                    setOrgMenuOpen(false);
                    auth.connect();
                  }}
                  style={menuBtn}
                >
                  Switch org…
                </button>
                <button
                  onClick={() => {
                    setOrgMenuOpen(false);
                    void auth.disconnect();
                  }}
                  style={menuBtn}
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>
        ) : auth.curator?.logged_in ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="chip soft-violet" style={{ fontSize: 10 }}>
              Q Branch · {auth.curator.email}
            </span>
            <button
              onClick={() => void auth.curatorLogout()}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "white",
                padding: "4px 8px",
                borderRadius: 6,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Log out
            </button>
          </div>
        ) : (
          <button
            onClick={() => auth.connect()}
            style={{
              background: "white",
              color: "var(--eb-15)",
              border: 0,
              padding: "5px 12px",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Connect org
          </button>
        )}
      </div>
    </nav>
  );
}

function NotifBell({ open, setOpen }: { open: boolean; setOpen: (v: boolean) => void }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Poll the notifications endpoint frequently. Successful runs and
  // failures both arrive via /api/notifications, and the bell needs to
  // light up within a few seconds of completion — not after the next
  // 30s tick.
  const { data: notifs = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: notificationsApi.list,
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
  const unread = (notifs ?? []).filter((n) => !n.read).length;
  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Notifications"
        style={{
          background: "transparent",
          border: 0,
          color: "white",
          padding: 6,
          cursor: "pointer",
          position: "relative",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 16v-5a6 6 0 10-12 0v5l-2 3h16z" strokeLinejoin="round" />
          <path d="M10 19a2 2 0 004 0" strokeLinecap="round" />
        </svg>
        {unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              minWidth: 14,
              height: 14,
              padding: "0 3px",
              background: "#ef4444",
              color: "white",
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 6px)",
            width: 340,
            background: "white",
            color: "var(--ink)",
            borderRadius: 10,
            border: "1px solid var(--bd)",
            boxShadow: "0 12px 32px -10px rgba(3,45,96,.35)",
            zIndex: 70,
            overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid var(--bd)" }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Notifications</div>
            {unread > 0 && (
              <button
                onClick={() => markAll.mutate()}
                style={{ background: "transparent", border: 0, color: "var(--eb-50)", fontSize: 11, cursor: "pointer" }}
              >
                Mark all read
              </button>
            )}
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto" }} className="thin-scroll">
            {(notifs ?? []).length === 0 && (
              <div style={{ padding: "20px 16px", color: "var(--ink-3)", fontSize: 12, textAlign: "center" }}>
                You're all caught up.
              </div>
            )}
            {(notifs ?? []).map((n) => (
              <div
                key={n.id}
                onClick={() => {
                  if (!n.read) markRead.mutate(n.id);
                  if (n.link) {
                    setOpen(false);
                    navigate(n.link.startsWith("http") ? "/runs" : n.link);
                  }
                }}
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid var(--bd-2)",
                  background: n.read ? "white" : "var(--cb-98)",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: n.read ? 500 : 600 }}>
                  {n.title ?? n.type ?? "Notification"}
                </div>
                {(n.body ?? n.message) && (
                  <div style={{ fontSize: 11, color: "var(--ink-2)", marginTop: 3 }}>
                    {n.body ?? n.message}
                  </div>
                )}
                {n.created_at && (
                  <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 4 }}>{n.created_at}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const menuBtn: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "8px 12px",
  background: "transparent",
  border: 0,
  textAlign: "left",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
};
